import { motion, useReducedMotion } from "framer-motion";
import { drawPath, easeOutSoft } from "@/lib/motion";

/* ================================================================== *
 * <ContourBackground>                                                 *
 *                                                                     *
 * A slow, decorative isopach — the sort of thing a resource geologist *
 * scribbles on a plan view. Concentric closed curves, off-centre,     *
 * with a soft drift. Sits behind the hero at low opacity.             *
 * ================================================================== */
export function ContourBackground({ className = "" }) {
  const reduce = useReducedMotion();
  // Deliberately organic — three nested closed splines, each a bit larger.
  const paths = [
    "M 90 320 C 60 220, 150 130, 260 130 S 490 180, 520 260 S 470 400, 340 420 S 130 420, 90 320 Z",
    "M 130 320 C 105 240, 175 165, 275 165 S 470 205, 495 275 S 445 385, 340 400 S 165 395, 130 320 Z",
    "M 175 320 C 155 260, 205 200, 290 200 S 445 230, 465 285 S 420 370, 340 380 S 195 375, 175 320 Z",
    "M 220 315 C 205 275, 240 235, 300 235 S 415 255, 430 295 S 400 355, 340 360 S 235 355, 220 315 Z",
    "M 260 315 C 250 290, 275 265, 315 265 S 390 275, 400 300 S 380 340, 340 342 S 268 340, 260 315 Z",
  ];
  return (
    <svg
      viewBox="0 0 600 500"
      className={`pointer-events-none absolute inset-0 w-full h-full ${className}`}
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="sres-contour-fade" cx="55%" cy="45%" r="60%">
          <stop offset="0%" stopColor="#1E6FFF" stopOpacity="0.10" />
          <stop offset="100%" stopColor="#1E6FFF" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect x="0" y="0" width="600" height="500" fill="url(#sres-contour-fade)" />
      {paths.map((d, i) => (
        <motion.path
          key={i}
          d={d}
          fill="none"
          stroke="#1E6FFF"
          strokeOpacity={0.14 + i * 0.04}
          strokeWidth="1"
          initial={reduce ? false : { pathLength: 0, opacity: 0 }}
          animate={reduce ? false : { pathLength: 1, opacity: 1 }}
          transition={reduce ? undefined : {
            pathLength: { duration: 2.2, ease: easeOutSoft, delay: i * 0.15 },
            opacity: { duration: 0.4, delay: i * 0.15 },
          }}
        />
      ))}
    </svg>
  );
}

/* ================================================================== *
 * <BlockModelViz>                                                     *
 *                                                                     *
 * A staggered voxel grid — reveals block-by-block, coloured by grade  *
 * (blue → teal → amber). Below the grid, faint contour ellipses on   *
 * the plan view "breathe" very slowly to give a sense of depth.       *
 * ================================================================== */
export function BlockModelViz() {
  const reduce = useReducedMotion();
  const cols = 12, rows = 6;
  const cellW = 22, cellH = 12;
  const offsetX = 60, offsetY = 82;

  // Grade function: peaks near a "vein" that diagonally crosses the model.
  const grade = (c, r) => {
    const dx = c - 6, dy = r - 3;
    const d = Math.sqrt(dx * dx * 0.6 + dy * dy);
    return Math.max(0, 1 - d / 5);
  };
  // Colour by grade: >0.7 amber, >0.4 teal, else blue.
  const fill = (g) =>
    g > 0.7 ? "#F59E0B"
    : g > 0.45 ? "#22C7B0"
    : "#1E6FFF";

  const cells = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const g = grade(c, r);
      // Diagonal wave order so the reveal feels like it sweeps across the model.
      const orderIndex = c + r;
      const delay = 0.15 + orderIndex * 0.045;
      cells.push(
        <motion.rect
          key={`${r}-${c}`}
          x={offsetX + c * cellW}
          y={offsetY + r * cellH}
          width={cellW - 2}
          height={cellH - 2}
          rx={1}
          fill={fill(g)}
          initial={reduce ? false : { opacity: 0, y: 6, scaleY: 0.6 }}
          animate={reduce ? false : { opacity: 0.35 + g * 0.55, y: 0, scaleY: 1 }}
          transition={reduce ? undefined : { duration: 0.5, ease: easeOutSoft, delay }}
          style={{ transformOrigin: `${offsetX + c * cellW}px ${offsetY + r * cellH + cellH / 2}px` }}
        />
      );
    }
  }

  return (
    <svg viewBox="0 0 380 200" className="w-full h-40" preserveAspectRatio="xMidYMid meet">
      {/* Plan-view contour rings — slowly breathing */}
      {[80, 60, 40, 22].map((r, i) => (
        <motion.ellipse
          key={i}
          cx="200"
          cy="120"
          rx={r * 2.1}
          ry={r * 0.85}
          fill="none"
          stroke="#1E6FFF"
          strokeOpacity={0.16 + i * 0.05}
          strokeWidth="1"
          animate={reduce ? false : { scale: [1, 1.03, 1] }}
          transition={reduce ? undefined : {
            duration: 6 + i,
            repeat: Infinity,
            ease: "easeInOut",
            delay: i * 0.3,
          }}
          style={{ transformOrigin: "200px 120px" }}
        />
      ))}
      {cells}
    </svg>
  );
}

/* ================================================================== *
 * <VariogramViz>                                                      *
 *                                                                     *
 * Experimental points pop into place, then the fitted spherical curve *
 * and its shaded envelope draw themselves.                            *
 * ================================================================== */
export function VariogramViz() {
  const reduce = useReducedMotion();
  const pts = [[8, 50], [22, 46], [36, 38], [52, 24], [70, 14], [92, 9], [112, 7]];
  return (
    <svg viewBox="0 0 120 60" className="mt-2 w-full h-14">
      <defs>
        <linearGradient id="sres-vgm-grad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#1E6FFF" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#1E6FFF" stopOpacity="0.05" />
        </linearGradient>
      </defs>
      {/* Fitted spherical envelope */}
      <motion.path
        d="M0,54 C15,52 30,44 50,26 C70,14 90,8 120,6 L120,60 L0,60 Z"
        fill="url(#sres-vgm-grad)"
        initial={reduce ? false : { opacity: 0 }}
        animate={reduce ? false : { opacity: 1 }}
        transition={reduce ? undefined : { duration: 0.8, delay: 1.6, ease: easeOutSoft }}
      />
      {/* Fitted spherical curve */}
      <motion.path
        d="M0,54 C15,52 30,44 50,26 C70,14 90,8 120,6"
        stroke="#1E6FFF"
        strokeWidth="1.6"
        fill="none"
        variants={drawPath(1.8, 0.6)}
        initial="hidden"
        animate="show"
      />
      {/* Experimental points */}
      {pts.map(([x, y], i) => (
        <motion.circle
          key={i}
          cx={x}
          cy={y}
          r="1.9"
          fill="#0F172A"
          initial={reduce ? false : { opacity: 0, scale: 0 }}
          animate={reduce ? false : { opacity: 1, scale: 1 }}
          transition={reduce ? undefined : {
            duration: 0.35,
            ease: easeOutSoft,
            delay: 0.2 + i * 0.07,
          }}
          style={{ transformOrigin: `${x}px ${y}px` }}
        />
      ))}
    </svg>
  );
}

/* ================================================================== *
 * <ClassificationBars>                                                *
 *                                                                     *
 * Classification percentages grow their own bar (Measured → Inferred).*
 * ================================================================== */
export function ClassificationBars({ rows }) {
  const reduce = useReducedMotion();
  return (
    <div className="mt-2 space-y-2 text-[12px]">
      {rows.map((r, i) => (
        <div key={r.label}>
          <div className="flex items-center justify-between">
            <span className="text-slate-600">{r.label}</span>
            <span className="text-brand-blue font-semibold font-mono">{r.pct}%</span>
          </div>
          <div className="mt-1 h-1 rounded-full bg-slate-100 overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-brand-blue"
              initial={reduce ? false : { width: 0 }}
              animate={reduce ? false : { width: `${r.pct}%` }}
              transition={reduce ? undefined : {
                duration: 1.2,
                ease: easeOutSoft,
                delay: 1.2 + i * 0.15,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ================================================================== *
 * <PipelineFlow>                                                      *
 *                                                                     *
 * Horizontal SVG "spine" that connects the workflow icons. A single   *
 * bright dot travels along the spine forever — subtle, but visually   *
 * ties the pipeline together and reinforces "one flow, one truth".   *
 * ================================================================== */
export function PipelineFlow({ nodeCount = 7 }) {
  // Path across the top of the icons.
  const width = 800, height = 60;
  const y = 30;
  const step = width / (nodeCount - 1);
  let d = `M 0 ${y}`;
  for (let i = 1; i < nodeCount; i++) {
    const x = i * step;
    d += ` L ${x} ${y}`;
  }

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="pointer-events-none absolute left-0 right-0 top-7 w-full h-16"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="pipeline-spine" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="#1E6FFF" stopOpacity="0.05" />
          <stop offset="15%" stopColor="#1E6FFF" stopOpacity="0.25" />
          <stop offset="85%" stopColor="#1E6FFF" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#1E6FFF" stopOpacity="0.05" />
        </linearGradient>
      </defs>
      {/* Static spine — no traveling dot */}
      <motion.path
        d={d}
        stroke="url(#pipeline-spine)"
        strokeWidth="1.5"
        fill="none"
        strokeDasharray="3 5"
        variants={drawPath(1.4, 0.2)}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.3 }}
      />
    </svg>
  );
}
